import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Search, MoreHorizontal, CreditCard, ArrowDownRight, DollarSign, TrendingUp } from "lucide-react";
import { subscriptionApi } from "@/lib/api/subscriptions";
import { useAuth } from "@clerk/clerk-react";
import type { Subscription, SubscriptionStatsResponse, Transaction } from "@/types/subscriptions";

export function SubscriptionsPage() {
  const { getToken } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  
  const [stats, setStats] = useState<SubscriptionStatsResponse | null>(null);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(true);

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = await getToken();
        if (!token) return;

        setLoading(true);

        const [statsData, transactionsData] = await Promise.all([
          subscriptionApi.getStats(token),
          subscriptionApi.getRecentTransactions(token),
        ]);

        setStats(statsData);
        setTransactions(transactionsData);
      } catch (error) {
        console.error("Error fetching subscription data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [getToken]);

  // Fetch subscriptions list when filters change
  useEffect(() => {
    const fetchList = async () => {
      try {
        const token = await getToken();
        if (!token) return;

        setListLoading(true);
        const data = await subscriptionApi.listAll(token, {
          status: statusFilter === "all" ? undefined : statusFilter,
          limit: 100, // Fetch more for client-side search if needed, or implement server-side search
        });
        setSubscriptions(data.subscriptions);
      } catch (error) {
        console.error("Error fetching subscriptions list:", error);
      } finally {
        setListLoading(false);
      }
    };

    fetchList();
  }, [getToken, statusFilter]);

  // Filter subscriptions client-side for search (since backend doesn't support search yet)
  const filteredSubscriptions = subscriptions.filter((sub) => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    return (
      sub.user?.email.toLowerCase().includes(searchLower) ||
      sub.user?.name.toLowerCase().includes(searchLower)
    );
  });

  const getStatusBadge = (status: string) => {
    const styles = {
      active: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100",
      trialing: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100",
      past_due: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100",
      canceled: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100",
      incomplete: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100",
    };
    return styles[status as keyof typeof styles] || styles.active;
  };

  const getPlanBadge = (plan: string) => {
    const styles = {
      Small: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100",
      Medium: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-100",
      Large: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-100",
      Enterprise: "bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-100",
    };
    return styles[plan as keyof typeof styles] || styles.Small;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          {loading ? (
            <div className="space-y-2">
              <Skeleton className="h-9 w-[200px]" />
              <Skeleton className="h-5 w-[300px]" />
            </div>
          ) : (
            <>
              <h2 className="text-3xl font-bold tracking-tight">Subscriptions</h2>
              <p className="text-muted-foreground">
                Manage subscriptions and track revenue
              </p>
            </>
          )}
        </div>
      </div>

      {/* Revenue Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            {loading ? (
               <Skeleton className="h-4 w-[100px]" />
            ) : (
               <>
                <CardTitle className="text-sm font-medium">MRR</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
               </>
            )}
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-[100px]" />
            ) : (
              <>
                <div className="text-2xl font-bold">{formatCurrency(stats?.mrr || 0)}</div>
                <div className="flex items-center text-xs text-green-600">
                  <TrendingUp className="mr-1 h-3 w-3" />
                  +18% {/* Hardcoded for now as we don't have historical data for growth */}
                </div>
              </>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            {loading ? (
               <Skeleton className="h-4 w-[140px]" />
            ) : (
               <>
                <CardTitle className="text-sm font-medium">Active Subscriptions</CardTitle>
                <CreditCard className="h-4 w-4 text-muted-foreground" />
               </>
            )}
          </CardHeader>
          <CardContent>
             {loading ? (
              <Skeleton className="h-8 w-[60px]" />
            ) : (
              <>
                <div className="text-2xl font-bold">{stats?.activeSubscriptions}</div>
                <p className="text-xs text-muted-foreground">Across all plans</p>
              </>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            {loading ? (
               <Skeleton className="h-4 w-[100px]" />
            ) : (
               <>
                <CardTitle className="text-sm font-medium">Churn Rate</CardTitle>
                <ArrowDownRight className="h-4 w-4 text-red-500" />
               </>
            )}
          </CardHeader>
          <CardContent>
             {loading ? (
              <Skeleton className="h-8 w-[60px]" />
            ) : (
              <>
                <div className="text-2xl font-bold">{stats?.churnRate}%</div>
                <p className="text-xs text-muted-foreground">Monthly churn</p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Plan Breakdown */}
        <Card className="lg:col-span-1">
          <CardHeader>
            {loading ? (
              <div className="space-y-2">
                <Skeleton className="h-6 w-[140px]" />
                <Skeleton className="h-4 w-[180px]" />
              </div>
            ) : (
              <>
                <CardTitle>Plan Distribution</CardTitle>
                <CardDescription>
                  Revenue breakdown by plan
                </CardDescription>
              </>
            )}
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
               {loading ? (
                 Array(3).fill(0).map((_, i) => (
                   <div key={i} className="space-y-2">
                     <div className="flex justify-between"><Skeleton className="h-4 w-[100px]" /><Skeleton className="h-4 w-[50px]" /></div>
                     <Skeleton className="h-2 w-full" />
                   </div>
                 ))
               ) : (
                  Object.entries(stats?.byPlan || {}).map(([plan, count]) => {
                    const total = stats?.totalSubscriptions || 1;
                    const percentage = Math.round((count / total) * 100);
                    return (
                      <div key={plan} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary" className={getPlanBadge(plan)}>
                              {plan}
                            </Badge>
                            <span className="text-sm text-muted-foreground">
                              {count} users
                            </span>
                          </div>
                        </div>
                        <div className="h-2 w-full rounded-full bg-secondary">
                          <div 
                            className="h-2 rounded-full bg-primary" 
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })
               )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Transactions */}
        <Card className="lg:col-span-2">
          <CardHeader>
            {loading ? (
              <div className="space-y-2">
                <Skeleton className="h-6 w-[160px]" />
                <Skeleton className="h-4 w-[240px]" />
              </div>
            ) : (
              <>
                <CardTitle>Recent Transactions</CardTitle>
                <CardDescription>
                  Latest payments and subscription events
                </CardDescription>
              </>
            )}
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {loading ? (
                 Array(4).fill(0).map((_, i) => (
                   <div key={i} className="flex justify-between py-2 border-b last:border-0">
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-[150px]" />
                        <Skeleton className="h-3 w-[100px]" />
                      </div>
                      <div className="flex gap-4">
                        <Skeleton className="h-4 w-[60px]" />
                        <Skeleton className="h-5 w-[80px]" />
                      </div>
                   </div>
                 ))
              ) : (
                transactions.map((transaction) => (
                  <div key={transaction.id} className="flex items-center justify-between py-2 border-b last:border-0">
                    <div>
                      <p className="font-medium">{transaction.customerEmail}</p>
                      <p className="text-sm text-muted-foreground capitalize">{transaction.type}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-medium">
                        {formatCurrency(transaction.amount)}
                      </span>
                      <Badge 
                        variant="secondary" 
                        className={transaction.status === "succeeded" 
                          ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100" 
                          : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100"
                        }
                      >
                        {transaction.status}
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        {new Date(transaction.created).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search subscriptions by name or email..."
                className="pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              {["all", "active", "trialing", "past_due", "canceled"].map((status) => (
                <Button
                  key={status}
                  variant={statusFilter === status ? "default" : "outline"}
                  size="sm"
                  onClick={() => setStatusFilter(status)}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Subscriptions Table */}
      <Card>
        <CardHeader>
           {listLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-6 w-[150px]" />
              <Skeleton className="h-4 w-[250px]" />
            </div>
          ) : (
            <>
              <CardTitle>All Subscriptions</CardTitle>
              <CardDescription>
                Showing {filteredSubscriptions.length} subscriptions
              </CardDescription>
            </>
          )}
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Videos/Mo</TableHead>
                <TableHead>Period</TableHead>
                <TableHead>Renews</TableHead>
                {/* <TableHead className="w-[50px]"></TableHead> */}
              </TableRow>
            </TableHeader>
            <TableBody>
              {listLoading ? (
                 Array(5).fill(0).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell><Skeleton className="h-10 w-[200px]" /></TableCell>
                      <TableCell><Skeleton className="h-6 w-[80px]" /></TableCell>
                      <TableCell><Skeleton className="h-6 w-[80px]" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-[50px]" /></TableCell>
                      <TableCell><Skeleton className="h-8 w-[150px]" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-[80px]" /></TableCell>
                      <TableCell><Skeleton className="h-8 w-[30px]" /></TableCell>
                    </TableRow>
                 ))
              ) : (
                filteredSubscriptions.map((sub) => (
                  <TableRow key={sub.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{sub.user?.name || "Unknown"}</p>
                        <p className="text-sm text-muted-foreground">{sub.user?.email || "No email"}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className={getPlanBadge(sub.planName)}>
                        {sub.planName}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className={getStatusBadge(sub.status)}>
                        {sub.status}
                      </Badge>
                    </TableCell>
                    <TableCell>{sub.videosPerMonth}</TableCell>
                    <TableCell>
                      <div className="text-xs">
                        <div>{sub.currentPeriodStart ? new Date(sub.currentPeriodStart).toLocaleDateString() : "-"}</div>
                        <div className="text-muted-foreground">to</div>
                        <div>{sub.currentPeriodEnd ? new Date(sub.currentPeriodEnd).toLocaleDateString() : "-"}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {sub.cancelAtPeriodEnd ? (
                        <span className="text-red-600 text-sm">Cancels</span>
                      ) : (
                        <span className="text-green-600 text-sm">Auto-renews</span>
                      )}
                    </TableCell>
                    {/* <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem>View Details</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-red-600">
                            Cancel Subscription
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell> */}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
